import React, { useState, useEffect } from 'react';
import { getAllocationsHistory, AllocationAudit } from '../services/allocationService';
import { getStaff, Staff } from '../services/staffService';
import { formatCurrency } from '../lib/formatters';

interface Props {
  tenantId: string;
}

export const AllocationsHistory: React.FC<Props> = ({ tenantId }) => {
  const [history, setHistory] = useState<AllocationAudit[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [data, staff] = await Promise.all([
        getAllocationsHistory(tenantId),
        getStaff(tenantId)
      ]);
      setStaffList(staff);
      // Assuming createdAt is a Firestore timestamp or similar
      setHistory(
        [...data].sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateB - dateA;
        })
      );
      setLoading(false);
    };
    fetchData();
  }, [tenantId]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading history...</div>;

  return (
    <div className="card overflow-hidden">
      <div className="p-6 md:p-8 border-b border-gray-50 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Allocation History</h3>
          <p className="text-xs text-gray-400 font-medium mt-0.5">Historical breakdown of distributed employee tip records</p>
        </div>
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
          {history.length} Records
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50/50">
              <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Date</th>
              <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Master TX ID</th>
              <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Staff Member</th>
              <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {history.length === 0 && (
              <tr>
                <td colSpan={4} className="px-8 py-20 text-center text-gray-400 font-medium">No history found.</td>
              </tr>
            )}
            {history.map(item => {
              const matchedStaff = staffList.find(s => 
                (item.staffId && s.id === item.staffId) || 
                (s.name && item.staffName && s.name.toLowerCase() === item.staffName.toLowerCase())
              );
              const resolvedPosId = item.posId || matchedStaff?.posId;
              const resolvedStaffId = item.staffId || matchedStaff?.id;

              return (
                <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-gray-600 font-medium whitespace-nowrap">{item.tipDate}</td>
                  <td className="px-6 py-4 font-mono text-[10px] text-gray-400 relative whitespace-nowrap">
                      <span title={item.masterTxId}>{item.masterTxId ? item.masterTxId.slice(-8) : (item.id ? item.id.slice(-8) : '-')}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900 text-sm">{item.staffName || 'Unknown Staff'}</span>
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        {resolvedPosId && (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                            POS ID: {resolvedPosId}
                          </span>
                        )}
                        {resolvedStaffId && (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                            Staff ID: {resolvedStaffId.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
